import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@components/ui/accordion';
import { Button } from '@components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import { Label } from '@components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select';
import errorsValidation from '@errors/validation';
import formatterDecimal from '@formatters/decimal';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@lib/ui/cn';
import BigNumber from 'bignumber.js';
import { CircleHelp, Dot, Plus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { NumberFormatBase } from 'react-number-format';
import { z } from 'zod';

import { useCalculator } from '../context';

interface IOption {
  label: string;
  value: string;
}

export interface IPediatricsContentProps {
  applicationText?: string;
  applicationUnitMeasure?: string;
  applicationUnitMeasure2?: string;
  applicationUnitSubtitle?: string;
  chargeDefault?: number;
  chargeTwoDefault?: number;
  conditions?: string[];
  description?: string;
  diluteDefault?: number;
  diluteText?: string;
  diluteUnitMeasureDefault?: string;
  diluteUnitMeasureOptions?: IOption[];
  doseDefault?: number;
  doseFixed?: number;
  doseTwoCalc2?: boolean;
  doseTwoDefault?: number;
  doseUnitMeasure?: string;
  drugDefault?: string;
  drugFieldWidth?: number;
  drugNameBig?: boolean;
  drugOptions?: IOption[];
  hideChargeThree?: boolean;
  hideDoseTwoConditionText?: boolean;
  max?: number;
  multiplyDoseMax?: number;
  multiplyDoseTwoMax?: number;
  nebMax?: number;
  observations?: string[];
  salineDefault?: number;
  salineLabel?: string;
  showBIC?: boolean;
  showDoseTwo?: boolean;
  showSpeed?: boolean;
  speedCalc2?: boolean;
  speedText?: string;
  speedUnitMeasure?: string;
  suggestions?: string[];
  templateEight?: boolean;
  templateFive?: boolean;
  templateFour?: boolean;
  templateFourCalc2?: boolean;
  templateOne?: boolean;
  templateOneCalc2?: boolean;
  templateOneCalc3?: boolean;
  templateSeven?: boolean;
  templateSevenCalc10?: boolean;
  templateSevenCalc11?: boolean;
  templateSevenCalc12?: boolean;
  templateSevenCalc2?: boolean;
  templateSevenCalc3?: boolean;
  templateSevenCalc4?: boolean;
  templateSevenCalc5?: boolean;
  templateSevenCalc6?: boolean;
  templateSevenCalc7?: boolean;
  templateSevenCalc8?: boolean;
  templateSevenCalc9?: boolean;
  templateSix?: boolean;
  templateSixCalc2?: boolean;
  templateSixCalcDrops?: boolean;
  templateThree?: boolean;
  templateTwo?: boolean;
  title: string;
  useDrugMl?: boolean;
}

const formSchema = z.object({
  drug: z.string().min(1, {
    message: errorsValidation.required,
  }),
  dilute: z.string().min(1, {
    message: errorsValidation.required,
  }),
  diluteUnitMeasure: z.string().min(1, {
    message: errorsValidation.required,
  }),
  saline: z.string().min(1, {
    message: errorsValidation.required,
  }),
  dose: z.string().min(1, {
    message: errorsValidation.required,
  }),
  doseTwo: z.string().min(1, {
    message: errorsValidation.required,
  }),
  charge: z.string().min(1, {
    message: errorsValidation.required,
  }),
  chargeTwo: z.string().min(1, {
    message: errorsValidation.required,
  }),
});

function Content({
  applicationText,
  applicationUnitMeasure = '',
  applicationUnitMeasure2 = '',
  applicationUnitSubtitle,
  chargeDefault = 0,
  chargeTwoDefault = 0,
  conditions = [],
  description = '',
  diluteDefault = 0,
  diluteText = 'Diluir',
  diluteUnitMeasureDefault = '1',
  diluteUnitMeasureOptions = [],
  doseDefault = 0,
  doseFixed = 1,
  doseTwoCalc2,
  doseTwoDefault = 0,
  doseUnitMeasure = 'mg/Kg',
  drugDefault = '',
  drugOptions = [],
  hideChargeThree = false,
  hideDoseTwoConditionText = false,
  max,
  multiplyDoseMax,
  multiplyDoseTwoMax,
  nebMax,
  observations = [],
  salineDefault = 0,
  salineLabel = '',
  showBIC = false,
  showDoseTwo = false,
  showSpeed = false,
  speedCalc2 = false,
  speedText = 'Infundir',
  speedUnitMeasure = '',
  suggestions = [],
  templateEight = false,
  templateFive = false,
  templateFour = false,
  templateFourCalc2 = false,
  templateOne = false,
  templateOneCalc2 = false,
  templateOneCalc3 = false,
  templateSeven = false,
  templateSevenCalc2 = false,
  templateSevenCalc3 = false,
  templateSevenCalc4 = false,
  templateSevenCalc5 = false,
  templateSevenCalc6 = false,
  templateSevenCalc7 = false,
  templateSevenCalc8 = false,
  templateSevenCalc9 = false,
  templateSevenCalc10 = false,
  templateSevenCalc11 = false,
  templateSix = false,
  templateSixCalc2 = false,
  templateSixCalcDrops = false,
  templateThree = false,
  templateTwo = false,
  title,
  useDrugMl = false,
}: IPediatricsContentProps) {
  const { ageMonth, ageYear, weight } = useCalculator();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    defaultValues: {
      drug: drugDefault,
      dilute: diluteDefault.toFixed(1),
      diluteUnitMeasure: diluteUnitMeasureDefault,
      saline: salineDefault.toFixed(1),
      dose: doseDefault.toFixed(doseFixed),
      doseTwo: doseTwoDefault.toFixed(doseFixed),
      charge: chargeDefault.toFixed(1),
      chargeTwo: chargeTwoDefault.toFixed(1),
    },
  });

  const [selectedDrug, setSelectedDrug] = useState<string>(drugDefault);
  const [selectedDiluteUnitMeasure, setSelectedDiluteUnitMeasure] =
    useState<string>(diluteUnitMeasureDefault);

  const [saline, setSaline] = useState<number>(salineDefault);
  const [dose, setDose] = useState<number>(doseDefault);
  const [doseTwo, setDoseTwo] = useState<number>(doseTwoDefault);
  const [dilute, setDilute] = useState<number>(diluteDefault);

  const [charge, setCharge] = useState<number>(chargeDefault);
  const [chargeTwo, setChargeTwo] = useState<number>(chargeTwoDefault);

  const [applicationValue, setApplicationValue] = useState<number>(0);
  const [applicationValueTwo, setApplicationValueTwo] = useState<number>(0);
  const [applicationValueThree, setApplicationValueThree] =
    useState<string>('');
  const [speed, setSpeed] = useState<number>(0);
  const [bic, setBic] = useState<number>(0);

  const [shock, setShock] = useState<number>(0);
  const [shockTwo, setShockTwo] = useState<number>(0);
  const [shockThree, setShockThree] = useState<number>(0);

  const [neb, setNeb] = useState<number>(0);
  const [spray, setSpray] = useState<string>('0 puffs');
  const [inal, setInal] = useState<string>('0 puffs');

  const [comp, setComp] = useState<number>(0);

  const [uValueOne, setUValueOne] = useState<string>('0');
  const [uValueTwo, setUValueTwo] = useState<string>('0');

  const getDrug = useCallback((): {
    drops: number;
    max: number | null;
    mg: number;
    ml: number;
  } => {
    if (!selectedDrug) {
      return {
        max: null,
        mg: 0,
        ml: 0,
        drops: 0,
      };
    }
    const drug = JSON.parse(selectedDrug) as {
      drops: number;
      max: number | null;
      mg: number;
      ml: number;
    };
    return drug;
  }, [selectedDrug]);

  const getDiluteUnitMeasure = useCallback((): string => {
    if (!selectedDiluteUnitMeasure) {
      return '';
    }
    return selectedDiluteUnitMeasure;
  }, [selectedDiluteUnitMeasure]);

  const handleDrugSelect = useCallback((drug: string): void => {
    setSelectedDrug(drug);
  }, []);

  const handleChange = useCallback(
    ({
      set,
      value,
    }: {
      set: Dispatch<SetStateAction<number>>;
      value: string;
    }): void => {
      if (!value) {
        set(0);
      }
      const valueParsed = Number(value.replace(',', '.'));
      set(valueParsed);
    },
    [],
  );

  const calculateApplication = useCallback((): void => {
    const diluteUnitMeasure = getDiluteUnitMeasure();
    const drug = getDrug();
    const diluteParsed = BigNumber(dilute);
    const doseParsed = BigNumber(dose);
    const drugMgParsed = BigNumber(drug.mg);
    const drugMlParsed = BigNumber(drug.ml);
    const salineParsed = BigNumber(saline);
    const weightParsed = BigNumber(weight);
    if (templateOneCalc2) {
      let part1 = BigNumber(doseParsed.multipliedBy(weightParsed));
      if (multiplyDoseMax && part1.toNumber() > multiplyDoseMax) {
        part1 = BigNumber(multiplyDoseMax);
      }
      const part2 = BigNumber(part1.multipliedBy(salineParsed));
      const part3 = BigNumber(diluteParsed.multipliedBy(drugMgParsed));
      const applicationValueCalculated = part2.dividedBy(part3);
      setApplicationValue(applicationValueCalculated.toNumber());
    } else if (templateOneCalc3) {
      let part1 = BigNumber(doseParsed.multipliedBy(weightParsed));
      if (multiplyDoseMax && part1.toNumber() > multiplyDoseMax) {
        part1 = BigNumber(multiplyDoseMax);
      }
      const part2 = BigNumber(diluteParsed.multipliedBy(drugMgParsed));
      const part3 = BigNumber(part2.dividedBy(salineParsed));
      const applicationValueCalculated = part1.dividedBy(part3);
      setApplicationValue(applicationValueCalculated.toNumber());
    } else {
      if (diluteUnitMeasure === '1') {
        let part1 = BigNumber(doseParsed.multipliedBy(weightParsed));
        if (multiplyDoseMax && part1.toNumber() > multiplyDoseMax) {
          part1 = BigNumber(multiplyDoseMax);
        }
        const part2 = BigNumber(drugMlParsed.multipliedBy(diluteParsed));
        const part3 = BigNumber(part2.plus(salineParsed));
        const part5 = BigNumber(diluteParsed.multipliedBy(drugMgParsed));
        const applicationValueCalculated = part1
          .multipliedBy(part3)
          .dividedBy(part5);
        setApplicationValue(applicationValueCalculated.toNumber());
      }
      if (diluteUnitMeasure === '2') {
        let part1 = BigNumber(doseParsed.multipliedBy(weightParsed));
        if (multiplyDoseMax && part1.toNumber() > multiplyDoseMax) {
          part1 = BigNumber(multiplyDoseMax);
        }
        const part2 = BigNumber(diluteParsed.plus(salineParsed));
        const part3 = BigNumber(drugMgParsed.dividedBy(drugMlParsed));
        const part4 = BigNumber(part3.multipliedBy(diluteParsed));
        const applicationValueCalculated = part1
          .multipliedBy(part2)
          .dividedBy(part4);
        setApplicationValue(applicationValueCalculated.toNumber());
      }
    }
  }, [
    dilute,
    dose,
    getDiluteUnitMeasure,
    getDrug,
    multiplyDoseMax,
    saline,
    templateOneCalc2,
    templateOneCalc3,
    weight,
  ]);

  const calculateApplicationTwo = useCallback((): void => {
    const drug = getDrug();
    const doseTwoParsed = BigNumber(doseTwo);
    const drugMgParsed = BigNumber(drug.mg);
    const drugMlParsed = BigNumber(drug.ml);
    const weightParsed = BigNumber(weight);
    if (doseTwoCalc2) {
      const diluteUnitMeasure = getDiluteUnitMeasure();
      const diluteParsed = BigNumber(dilute);
      const salineParsed = BigNumber(saline);
      if (diluteUnitMeasure === '1') {
        let part1 = BigNumber(doseTwoParsed.multipliedBy(weightParsed));
        if (multiplyDoseTwoMax && part1.toNumber() > multiplyDoseTwoMax) {
          part1 = BigNumber(multiplyDoseTwoMax);
        }
        const part2 = BigNumber(drugMlParsed.multipliedBy(diluteParsed));
        const part3 = BigNumber(part2.plus(salineParsed));
        const part5 = BigNumber(diluteParsed.multipliedBy(drugMgParsed));
        const applicationValueCalculated = part1
          .multipliedBy(part3)
          .dividedBy(part5);
        setApplicationValueTwo(applicationValueCalculated.toNumber());
      }
      if (diluteUnitMeasure === '2') {
        let part1 = BigNumber(doseTwoParsed.multipliedBy(weightParsed));
        if (multiplyDoseTwoMax && part1.toNumber() > multiplyDoseTwoMax) {
          part1 = BigNumber(multiplyDoseTwoMax);
        }
        const part2 = BigNumber(diluteParsed.plus(salineParsed));
        const part3 = BigNumber(drugMgParsed.dividedBy(drugMlParsed));
        const part4 = BigNumber(part3.multipliedBy(diluteParsed));
        const applicationValueCalculated = part1
          .multipliedBy(part2)
          .dividedBy(part4);
        setApplicationValueTwo(applicationValueCalculated.toNumber());
      }
      return;
    }
    let part1 = BigNumber(doseTwoParsed.multipliedBy(weightParsed));
    if (multiplyDoseTwoMax && part1.toNumber() > multiplyDoseTwoMax) {
      part1 = BigNumber(multiplyDoseTwoMax);
    }
    const part2 = BigNumber(part1.multipliedBy(drugMlParsed));
    const applicationValueCalculated = BigNumber(part2.dividedBy(drugMgParsed));
    setApplicationValueTwo(applicationValueCalculated.toNumber());
  }, [
    dilute,
    doseTwo,
    doseTwoCalc2,
    getDiluteUnitMeasure,
    getDrug,
    multiplyDoseTwoMax,
    saline,
    weight,
  ]);

  const calculateApplicationThree = useCallback((): void => {
    if (templateSevenCalc2) {
      if (weight <= 8) {
        setApplicationValueThree('0,1 a 0,2mL IM');
      }
      if (weight > 8 && weight < 16) {
        setApplicationValueThree('0,2 a 0,5mL IV ou IM');
      }
      if (weight >= 16 && weight <= 23) {
        setApplicationValueThree('0,3 a 0,8mL IV ou IM');
      }
      if (weight >= 24 && weight <= 30) {
        setApplicationValueThree('0,4 a 1mL IV ou IM');
      }
      if (weight >= 31 && weight <= 45) {
        setApplicationValueThree('0,5 a 1,5mL IV ou IM');
      }
      if (weight > 45 && weight <= 53) {
        setApplicationValueThree('0,8 a 1,5mL IV ou IM');
      }
      if (weight > 53) {
        setApplicationValueThree('2mL IV ou IM');
      }
    } else if (templateSevenCalc3) {
      if (weight < 8) {
        setApplicationValueThree('Evitar');
      }
      if (weight >= 8 && weight <= 15) {
        setApplicationValueThree(
          `Tomar meio cp de 4mg ${applicationUnitMeasure}`,
        );
      }
      if (weight > 15 && weight <= 30) {
        setApplicationValueThree(`Tomar 1 cp de 4mg ${applicationUnitMeasure}`);
      }
      if (weight >= 30) {
        setApplicationValueThree(`Tomar 1 cp de 8mg ${applicationUnitMeasure}`);
      }
    } else if (templateSevenCalc4) {
      if (ageYear < 2) {
        setApplicationValueThree('Evitar');
      }
      if (ageYear >= 2 && ageYear <= 6) {
        setApplicationValueThree(
          'Dar 5-10mL VO a cada 6-8 horas, se necessário (máx 30mL/dia)',
        );
      }
      if (ageYear >= 7 && ageYear <= 12) {
        setApplicationValueThree(
          'Dar 10-20mL VO a cada 6-8 horas, se necessário (máx 60mL/dia)',
        );
      }
      if (ageYear >= 13) {
        setApplicationValueThree(
          'Dar 20-40mL VO a cada 4-6 horas, se necessário (máx 160mL/dia)',
        );
      }
    } else if (templateSevenCalc5) {
      if (weight <= 8) {
        setApplicationValueThree('Dar 1 a 2 ml VO');
      }
      if (weight >= 9 && weight <= 14) {
        setApplicationValueThree('Dar 2 a 4 ml VO');
      }
      if (weight >= 15 && weight <= 20) {
        setApplicationValueThree('Dar 3 a 6 ml VO');
      }
      if (weight >= 21 && weight <= 26) {
        setApplicationValueThree('Dar 4 a 7 ml VO');
      }
      if (weight >= 21 && weight <= 26) {
        setApplicationValueThree('Dar 4 a 7 ml VO');
      }
      if (weight >= 27 && weight <= 32) {
        setApplicationValueThree('Dar 5 a 7 ml VO');
      }
      if (weight >= 33 && weight <= 38) {
        setApplicationValueThree('Dar 6 a 7 ml VO');
      }
      if (weight >= 39) {
        setApplicationValueThree('Dar 7 ml VO');
      }
    } else if (templateSevenCalc6) {
      if (weight >= 30) {
        setApplicationValueThree('Dar 10mL VO 1x/dia por 5 dias');
      }
      if (weight < 30) {
        setApplicationValueThree('Dar 5mL VO 1x/dia por 5 dias');
      }
      if (ageYear < 2) {
        setApplicationValueThree('Evitar (preferir desloratadina)');
      }
    } else if (templateSevenCalc7) {
      if (ageYear === 0 && ageMonth < 6) {
        setApplicationValueThree('Evitar (indicado para maiores de 6 meses)');
      }
      if (ageYear === 0 && ageMonth >= 6 && ageMonth <= 11) {
        setApplicationValueThree('Dar 2mL VO 1x/dia por 5 dias');
      }
      if ((ageYear >= 1 && ageYear <= 5) || ageMonth === 12) {
        setApplicationValueThree('Dar 2,5mL VO 1x/dia por 5 dias');
      }
      if (ageYear >= 6 && ageYear <= 11) {
        setApplicationValueThree('Dar 5mL VO 1x/dia por 5 dias');
      }
      if (ageYear >= 12) {
        setApplicationValueThree('Dar 10mL VO 1x/dia por 5 dias');
      }
    } else if (templateSevenCalc8) {
      const drug = getDrug();
      if (ageYear < 4) {
        setApplicationValueThree(
          'Evitar (indicado para maiores de 4 anos segundo a bula do profissional)',
        );
        return;
      }
      if (drug.mg === 32) {
        setApplicationValueThree(
          'Aplicar 1-2 jatos/narina a cada 12h ou 1-4 jatos/narina a cada 24h',
        );
      }
      if (drug.mg === 64) {
        setApplicationValueThree(
          'Aplicar 1 jato/narina a cada 12h ou 1-2 jatos/narina a cada 24h',
        );
      }
    } else if (templateSevenCalc9) {
      if (ageYear < 2) {
        setApplicationValueThree(
          'Evitar (indicado para maiores de 2 anos segundo a bula do profissional)',
        );
        return;
      }
      setApplicationValueThree('Aplicar 1-2 jatos/narina a cada 24h');
    } else if (templateSevenCalc10) {
      if (ageYear < 1) {
        setApplicationValueThree(
          'Dar 5mL uma vez ao dia, ajustar dose para obter 2-3 evacuações por dia',
        );
      }
      if (ageYear >= 1 && ageYear <= 5) {
        setApplicationValueThree(
          'Dar 5-10mL uma vez ao dia, ajustar dose para obter 2-3 evacuações por dia',
        );
      }
      if (ageYear >= 6 && ageYear <= 12) {
        setApplicationValueThree(
          'Dar 10-15mL uma vez ao dia, ajustar dose para obter 2-3 evacuações por dia',
        );
      }
      if (ageYear > 12) {
        setApplicationValueThree(
          'Tomar 15-30mL uma vez ao dia, ajustar dose para obter 2-3 evacuações por dia',
        );
      }
    } else if (templateSevenCalc11) {
      setApplicationValueThree(
        'Aplicar via retal, 1 sup/dia, até atingir 1 evacuação/dia',
      );
    } else {
      const weightParsed = BigNumber(weight);
      if (ageYear >= 12) {
        setApplicationValueThree((0.5).toFixed(1));
      } else {
        const applicationValueCalculated = BigNumber(
          BigNumber(0.01).multipliedBy(weightParsed),
        ).toNumber();
        if (applicationValueCalculated > 0.3) {
          setApplicationValueThree((0.3).toFixed(1));
        } else {
          setApplicationValueThree(applicationValueCalculated.toFixed(1));
        }
      }
    }
  }, [
    ageMonth,
    ageYear,
    applicationUnitMeasure,
    getDrug,
    templateSevenCalc10,
    templateSevenCalc11,
    templateSevenCalc2,
    templateSevenCalc3,
    templateSevenCalc4,
    templateSevenCalc5,
    templateSevenCalc6,
    templateSevenCalc7,
    templateSevenCalc8,
    templateSevenCalc9,
    weight,
  ]);

  const calculateShock = useCallback((): void => {
    const weightParsed = BigNumber(weight);
    const chargeParsed = BigNumber(charge);
    const shockCalculated = BigNumber(chargeParsed.multipliedBy(weightParsed));
    setShock(shockCalculated.toNumber());
  }, [charge, weight]);

  const calculateShockTwo = useCallback((): void => {
    const weightParsed = BigNumber(weight);
    const chargeTwoParsed = BigNumber(chargeTwo);
    const shockTwoCalculated = BigNumber(
      chargeTwoParsed.multipliedBy(weightParsed),
    );
    setShockTwo(shockTwoCalculated.toNumber());
  }, [chargeTwo, weight]);

  const calculateShockThree = useCallback((): void => {
    const weightParsed = BigNumber(weight);
    const chargeTwoParsed = BigNumber(chargeTwo);
    const shockThreeCalculated = BigNumber(
      chargeTwoParsed.multipliedBy(weightParsed),
    );
    setShockThree(shockThreeCalculated.toNumber());
  }, [chargeTwo, weight]);

  const calculateNeb = useCallback((): void => {
    const doseParsed = BigNumber(dose);
    const weightParsed = BigNumber(weight);
    const drug = getDrug();
    const drugMgParsed = BigNumber(drug.mg);
    const drugDropsParsed = BigNumber(drug.drops || drug.ml);
    let part1 = BigNumber(doseParsed.multipliedBy(weightParsed));
    if (multiplyDoseMax && part1.toNumber() > multiplyDoseMax) {
      part1 = BigNumber(multiplyDoseMax);
    }
    const nebCalculated = BigNumber(
      part1.multipliedBy(drugDropsParsed).dividedBy(drugMgParsed),
    );
    if (nebMax && nebCalculated.toNumber() > nebMax) {
      setNeb(nebMax);
    } else {
      setNeb(nebCalculated.toNumber());
    }
  }, [dose, getDrug, multiplyDoseMax, nebMax, weight]);

  const calculateSpray = useCallback((): void => {
    if (templateFourCalc2) {
      const weightParsed = BigNumber(weight);
      const sprayCalculated = weightParsed.dividedBy(3).toNumber();
      if (sprayCalculated > 10) {
        setSpray('10 puffs');
      } else {
        setSpray(`${sprayCalculated.toFixed(0)} puffs`);
      }
    } else {
      if (ageYear >= 12) {
        setSpray('2 a 4 puffs');
      } else {
        setSpray('1 a 2 puffs');
      }
    }
  }, [ageYear, templateFourCalc2, weight]);

  const calculateInal = useCallback((): void => {
    if (ageYear > 12) {
      setInal('40 gotas + 4mL SF 0,9%s');
    } else if (ageYear <= 12 && ageYear >= 6) {
      setInal('20 gotas + 4mL SF 0,9%');
    } else {
      setInal('8 a 20 gotas + 4mL SF 0,9%');
    }
  }, [ageYear]);

  const calculateSpeed = useCallback((): void => {
    if (speedCalc2) {
      const doseParsed = BigNumber(dose);
      const speedCalculated = doseParsed;
      setSpeed(speedCalculated.toNumber());
    } else {
      const drug = getDrug();
      const diluteParsed = BigNumber(dilute);
      const drugMgParsed = BigNumber(drug.mg);
      const drugMlParsed = BigNumber(drug.ml);
      const salineParsed = BigNumber(saline);
      const weightParsed = BigNumber(weight);
      const part1 = BigNumber(drugMgParsed.dividedBy(drugMlParsed));
      const part2 = BigNumber(diluteParsed.multipliedBy(part1));
      const part3 = BigNumber(BigNumber(125).multipliedBy(weightParsed));
      const part4 = BigNumber(salineParsed.plus(diluteParsed));
      const part5 = BigNumber(BigNumber(60).multipliedBy(part2).div(part3));
      const speedCalculated = BigNumber(
        part5.dividedBy(part4).multipliedBy(applicationValue),
      );
      setSpeed(speedCalculated.toNumber() || 0);
    }
  }, [applicationValue, dilute, dose, getDrug, saline, speedCalc2, weight]);

  const calculateComp = useCallback((): void => {
    const drug = getDrug();
    const drugMgParsed = BigNumber(drug.mg);
    const drugMlParsed = BigNumber(drug.ml);
    const doseParsed = BigNumber(dose);
    const weightParsed = BigNumber(weight);
    let compToSet = 0;
    let part1 = BigNumber(doseParsed.multipliedBy(weightParsed));
    if (multiplyDoseMax && part1.toNumber() > multiplyDoseMax) {
      part1 = BigNumber(multiplyDoseMax);
    }
    if (templateSixCalcDrops) {
      const drugDropsParsed = BigNumber(drug.drops);
      const part2 = useDrugMl
        ? BigNumber(drugMlParsed.dividedBy(drugMgParsed))
        : BigNumber(drugDropsParsed.dividedBy(drugMgParsed));
      const compCalculated = BigNumber(part1.multipliedBy(part2));
      compToSet = compCalculated.toNumber();
    } else if (templateSixCalc2) {
      const part1 = weightParsed.multipliedBy(doseParsed);
      const compCalculated = part1.dividedBy(BigNumber(30));
      compToSet = compCalculated.toNumber();
    } else {
      const part2 = useDrugMl
        ? BigNumber(drugMlParsed.dividedBy(drugMgParsed))
        : drugMgParsed;
      const compCalculated = BigNumber(
        useDrugMl ? part1.multipliedBy(part2) : part1.dividedBy(part2),
      );
      compToSet = compCalculated.toNumber();
    }
    if (max && compToSet > max) {
      setComp(max);
    } else if (drug.max && compToSet > drug.max) {
      setComp(drug.max);
    } else {
      setComp(compToSet);
    }
  }, [
    dose,
    getDrug,
    max,
    multiplyDoseMax,
    templateSixCalc2,
    templateSixCalcDrops,
    useDrugMl,
    weight,
  ]);

  const calculateBIC = useCallback((): void => {
    const applicationValueParsed = BigNumber(applicationValue);
    const part1 = BigNumber(applicationValueParsed.dividedBy(speed));
    const bicCalculated = BigNumber(part1.multipliedBy(60));
    setBic(bicCalculated.toNumber());
  }, [applicationValue, speed]);

  const calculateU = useCallback((): void => {
    if (weight <= 27) {
      setUValueOne('600.000');
    }
    if (weight > 27) {
      setUValueOne('1.200.000');
    }
    const weightParsed = BigNumber(weight);
    const uValueTwoCalculated = weightParsed
      .multipliedBy(BigNumber(50_000))
      .toNumber();
    if (uValueTwoCalculated > 2_400_000) {
      setUValueTwo('2.400.000');
    } else {
      setUValueTwo(
        uValueTwoCalculated
          .toString()
          .replace(/,/g, '')
          .replace(/\B(?=(\d{3})+(?!\d))/g, '.')
          .replace(/\.(?=\d{2}$)/, ','),
      );
    }
  }, [weight]);

  useEffect(() => {
    calculateApplication();
  }, [calculateApplication]);

  useEffect(() => {
    calculateApplicationTwo();
  }, [calculateApplicationTwo]);

  useEffect(() => {
    calculateApplicationThree();
  }, [calculateApplicationThree]);

  useEffect(() => {
    calculateShock();
  }, [calculateShock]);

  useEffect(() => {
    calculateShockTwo();
  }, [calculateShockTwo]);

  useEffect(() => {
    calculateShockThree();
  }, [calculateShockThree]);

  useEffect(() => {
    calculateNeb();
  }, [calculateNeb]);

  useEffect(() => {
    calculateSpray();
  }, [calculateSpray]);

  useEffect(() => {
    calculateInal();
  }, [calculateInal]);

  useEffect(() => {
    calculateSpeed();
  }, [calculateSpeed]);

  useEffect(() => {
    calculateComp();
  }, [calculateComp]);

  useEffect(() => {
    calculateBIC();
  }, [calculateBIC]);

  useEffect(() => {
    calculateU();
  }, [calculateU]);

  function onSubmit(_: z.infer<typeof formSchema>) {
    //
  }

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'drug' && value.drug) {
        handleDrugSelect(value.drug);
      }
      if (name === 'dilute' && value.dilute) {
        handleChange({
          set: setDilute,
          value: value.dilute,
        });
      }
      if (name === 'diluteUnitMeasure' && value.diluteUnitMeasure) {
        setSelectedDiluteUnitMeasure(value.diluteUnitMeasure);
      }
      if (name === 'saline' && value.saline) {
        handleChange({
          set: setSaline,
          value: value.saline,
        });
      }
      if (name === 'dose' && value.dose) {
        handleChange({
          set: setDose,
          value: value.dose,
        });
      }
      if (name === 'doseTwo' && value.doseTwo) {
        handleChange({
          set: setDoseTwo,
          value: value.doseTwo,
        });
      }
      if (name === 'charge' && value.charge) {
        handleChange({
          set: setCharge,
          value: value.charge,
        });
      }
      if (name === 'chargeTwo' && value.chargeTwo) {
        handleChange({
          set: setChargeTwo,
          value: value.chargeTwo,
        });
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  return (
    <Accordion className="mx-auto max-w-3xl" collapsible type="single">
      <AccordionItem value="item-1">
        <AccordionTrigger className="cursor-pointer text-lg uppercase hover:opacity-70">
          {title}
        </AccordionTrigger>

        <AccordionContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              {!templateTwo && (
                <div className="mb-4 flex flex-row justify-center gap-2">
                  <FormField
                    control={form.control}
                    name="drug"
                    render={({ field }) => (
                      <FormItem className="grid grid-cols-[auto_1fr] items-center gap-2">
                        <FormLabel className="text-tertiary">{title}</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}>
                          <FormControl>
                            <SelectTrigger onBlur={field.onBlur}>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>

                          <SelectContent>
                            {drugOptions.map((drugOption) => (
                              <SelectItem
                                className="focus:bg-tertiary"
                                key={drugOption.value}
                                value={drugOption.value}>
                                {drugOption.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                  {(suggestions.length > 0 ||
                    observations.length > 0 ||
                    conditions.length > 0) && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          className="cursor-pointer"
                          size="icon"
                          variant="ghost">
                          <CircleHelp
                            className="text-foreground"
                            strokeWidth={2}
                          />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 space-y-4">
                        <h1 className="text-secondary text-center font-medium">
                          {title}
                        </h1>
                        {suggestions.length > 0 && (
                          <div>
                            <Label className="text-tertiary">Sugestão</Label>
                            {suggestions.map((suggestion, index) => {
                              const hideDot = suggestion.startsWith('_');
                              const suggestionFormatted = suggestion.replace(
                                '_',
                                '',
                              );
                              return (
                                <div
                                  className="flex flex-row gap-1"
                                  key={index}>
                                  {!hideDot && (
                                    <Dot className="text-foreground" />
                                  )}
                                  <p
                                    className={cn(
                                      'text-foreground flex-1 text-sm',
                                      hideDot && 'ml-10',
                                    )}>
                                    {suggestionFormatted}
                                  </p>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {observations.length > 0 && (
                          <div>
                            <Label className="text-tertiary">Observação</Label>
                            {observations.map((observation, index) => (
                              <div className="flex flex-row gap-1" key={index}>
                                <Dot className="text-foreground" />
                                <p className="text-foreground flex-1 text-sm">
                                  {observation}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}

                        {conditions.length > 0 && (
                          <div>
                            <Label className="text-tertiary">Condições</Label>
                            {conditions.map((observation, index) => (
                              <div className="flex flex-row gap-1" key={index}>
                                <Dot className="text-foreground" />
                                <p className="text-foreground flex-1 text-sm">
                                  {observation}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              )}

              {templateOne && (
                <div className="mx-auto block w-fit">
                  <div className="mb-4 flex flex-row items-center gap-2">
                    <FormField
                      control={form.control}
                      name="dilute"
                      render={({ field: { ref, ...rest } }) => (
                        <FormItem className="grid grid-cols-[4rem_auto] items-center">
                          <FormLabel>{diluteText}</FormLabel>
                          <FormControl>
                            <NumberFormatBase
                              className="[&_input]:w-20 [&_input]:text-center"
                              customInput={Input}
                              format={(value) => formatterDecimal(value, 1)}
                              getCaretBoundary={(formattedValue) => {
                                const boundaryAry = Array.from({
                                  length: formattedValue.length + 1,
                                }).map(() => true);
                                boundaryAry[0] = false;
                                boundaryAry[1] = false;
                                boundaryAry[2] = false;
                                return boundaryAry;
                              }}
                              getInputRef={ref}
                              {...rest}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="diluteUnitMeasure"
                      render={({ field }) => (
                        <FormItem>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}>
                            <FormControl>
                              <SelectTrigger
                                className="min-w-28 border-0 shadow-none"
                                onBlur={field.onBlur}>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>

                            <SelectContent>
                              {diluteUnitMeasureOptions.map((drugOption) => (
                                <SelectItem
                                  className="focus:bg-tertiary"
                                  key={drugOption.value}
                                  value={drugOption.value}>
                                  {drugOption.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                    <Plus className="text-foreground size-6" />
                  </div>

                  <div className="mb-4 flex flex-row items-center gap-2">
                    <FormField
                      control={form.control}
                      name="saline"
                      render={({ field: { ref, ...rest } }) => (
                        <FormItem className="grid grid-cols-[4rem_auto_auto] items-center">
                          <FormLabel>{salineLabel}</FormLabel>
                          <FormControl>
                            <NumberFormatBase
                              className="[&_input]:w-20 [&_input]:text-center"
                              customInput={Input}
                              format={(value) => formatterDecimal(value, 1)}
                              getCaretBoundary={(formattedValue) => {
                                const boundaryAry = Array.from({
                                  length: formattedValue.length + 1,
                                }).map(() => true);
                                boundaryAry[0] = false;
                                boundaryAry[1] = false;
                                boundaryAry[2] = false;
                                return boundaryAry;
                              }}
                              getInputRef={ref}
                              {...rest}
                            />
                          </FormControl>
                          <FormDescription className="font-light">
                            mL
                          </FormDescription>
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="mb-4 flex flex-row items-center gap-2">
                    <FormField
                      control={form.control}
                      name="dose"
                      render={({ field: { ref, ...rest } }) => (
                        <FormItem className="grid grid-cols-[4rem_auto_auto] items-center">
                          <FormLabel>
                            {hideDoseTwoConditionText ? '1ª Dose' : 'Dose'}
                          </FormLabel>
                          <FormControl>
                            <NumberFormatBase
                              className="[&_input]:w-20 [&_input]:text-center"
                              customInput={Input}
                              format={(value) =>
                                formatterDecimal(value, doseFixed)
                              }
                              getCaretBoundary={(formattedValue) => {
                                const boundaryAry = Array.from({
                                  length: formattedValue.length + 1,
                                }).map(() => true);
                                boundaryAry[0] = false;
                                boundaryAry[1] = false;
                                boundaryAry[2] = false;
                                return boundaryAry;
                              }}
                              getInputRef={ref}
                              {...rest}
                            />
                          </FormControl>
                          <FormDescription className="font-light">
                            {doseUnitMeasure}
                          </FormDescription>
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="mb-4 grid grid-cols-[4rem_min-content_max-content] items-center gap-2">
                    {applicationText ? (
                      <Label>{applicationText}</Label>
                    ) : (
                      <Label>{templateOneCalc3 ? 'Dar' : 'Aplicar'}</Label>
                    )}
                    <p>
                      <span className="text-tertiary text-lg font-bold">
                        {applicationValue.toFixed(1)}
                      </span>{' '}
                    </p>
                    <Label className="text-foreground text-sm font-light">
                      {applicationUnitMeasure}
                    </Label>
                  </div>

                  {applicationUnitSubtitle && (
                    <div className="mb-4 grid grid-cols-[26rem_min-content_max-content]">
                      <Label className="text-foreground mt-4 text-center text-sm font-light">
                        {applicationUnitSubtitle}
                      </Label>
                    </div>
                  )}

                  {showDoseTwo && (
                    <div>
                      {!hideDoseTwoConditionText && (
                        <div className="mb-4 flex flex-row items-center justify-center">
                          <Label>OU</Label>
                        </div>
                      )}

                      <div
                        className={cn(
                          'mb-4 flex flex-row items-center gap-2',
                          hideDoseTwoConditionText ? 'mt-8' : '',
                        )}>
                        <FormField
                          control={form.control}
                          name="doseTwo"
                          render={({ field: { ref, ...rest } }) => (
                            <FormItem className="grid grid-cols-[4rem_auto_auto] items-center">
                              <FormLabel>
                                {hideDoseTwoConditionText ? '2ª Dose' : 'Dose'}
                              </FormLabel>
                              <FormControl>
                                <NumberFormatBase
                                  className="[&_input]:w-26 [&_input]:text-center"
                                  customInput={Input}
                                  format={(value) =>
                                    formatterDecimal(value, doseFixed)
                                  }
                                  getCaretBoundary={(formattedValue) => {
                                    const boundaryAry = Array.from({
                                      length: formattedValue.length + 1,
                                    }).map(() => true);
                                    boundaryAry[0] = false;
                                    boundaryAry[1] = false;
                                    boundaryAry[2] = false;
                                    return boundaryAry;
                                  }}
                                  getInputRef={ref}
                                  {...rest}
                                />
                              </FormControl>
                              <FormDescription className="font-light">
                                {hideDoseTwoConditionText
                                  ? 'mg/Kg'
                                  : 'mg/Kg (endotraqueal)'}
                              </FormDescription>
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="mb-4 grid grid-cols-[4rem_min-content_max-content] items-center gap-2">
                        <Label>Aplicar</Label>
                        <p className="font-light">
                          <span className="text-tertiary text-lg font-bold">
                            {applicationValueTwo.toFixed(1)}
                          </span>{' '}
                        </p>
                        <Label className="text-foreground text-sm font-light">
                          {hideDoseTwoConditionText
                            ? 'mL IV bolus'
                            : 'ml endotraqueal (sem diluir)'}
                        </Label>
                      </div>
                    </div>
                  )}

                  {showSpeed && (
                    <div className="mb-4 grid grid-cols-[4rem_min-content_max-content] items-center gap-2">
                      <Label>{speedText}</Label>
                      <p className="font-light">
                        <span className="text-tertiary text-lg font-bold">
                          {speedText ? speed : speed.toFixed(1)}
                        </span>{' '}
                      </p>
                      <Label className="text-foreground text-sm font-light">
                        {speedUnitMeasure}
                      </Label>
                    </div>
                  )}

                  {showBIC && (
                    <div className="mb-4 grid grid-cols-[4rem_min-content_max-content] items-center gap-2">
                      <Label>Na BIC</Label>
                      <p className="font-light">
                        <span className="text-tertiary text-lg font-bold">
                          {bic.toFixed(1)}
                        </span>{' '}
                      </p>
                      <Label className="text-foreground text-sm font-light">
                        ml/h (1mg/kg/min)
                      </Label>
                    </div>
                  )}
                </div>
              )}

              {templateTwo && (
                <div>
                  <div className="mb-4 flex flex-row items-center justify-center">
                    <Label>{description}</Label>
                  </div>
                  <div className="mb-4 flex flex-row items-center justify-center gap-2">
                    <FormField
                      control={form.control}
                      name="charge"
                      render={({ field: { ref, ...rest } }) => (
                        <FormItem className="flex flex-row items-center">
                          <FormLabel>Carga</FormLabel>
                          <FormControl>
                            <NumberFormatBase
                              className="[&_input]:w-26 [&_input]:text-center"
                              customInput={Input}
                              format={(value) => formatterDecimal(value, 1)}
                              getCaretBoundary={(formattedValue) => {
                                const boundaryAry = Array.from({
                                  length: formattedValue.length + 1,
                                }).map(() => true);
                                boundaryAry[0] = false;
                                boundaryAry[1] = false;
                                boundaryAry[2] = false;
                                return boundaryAry;
                              }}
                              getInputRef={ref}
                              {...rest}
                            />
                          </FormControl>
                          <FormDescription className="font-light">
                            J/Kg no 1º choque
                          </FormDescription>
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="mb-4 flex flex-row items-center justify-center gap-2">
                    <FormField
                      control={form.control}
                      name="chargeTwo"
                      render={({ field: { ref, ...rest } }) => (
                        <FormItem className="flex flex-row items-center">
                          <FormLabel>Carga</FormLabel>
                          <FormControl>
                            <NumberFormatBase
                              className="[&_input]:w-26 [&_input]:text-center"
                              customInput={Input}
                              format={(value) => formatterDecimal(value, 1)}
                              getCaretBoundary={(formattedValue) => {
                                const boundaryAry = Array.from({
                                  length: formattedValue.length + 1,
                                }).map(() => true);
                                boundaryAry[0] = false;
                                boundaryAry[1] = false;
                                boundaryAry[2] = false;
                                return boundaryAry;
                              }}
                              getInputRef={ref}
                              {...rest}
                            />
                          </FormControl>
                          <FormDescription className="font-light">
                            J/Kg no 2º choque
                          </FormDescription>
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="mb-4 flex flex-row items-center justify-center gap-2">
                    <Label>1º choque</Label>
                    <p className="font-light">
                      <span className="text-tertiary text-lg font-bold">
                        {shock.toFixed(1)}
                      </span>{' '}
                    </p>
                    <Label className="text-foreground text-sm font-light">
                      J
                    </Label>
                  </div>

                  <div className="mb-4 flex flex-row items-center justify-center gap-2">
                    <Label>2º choque</Label>
                    <p className="font-light">
                      <span className="text-tertiary text-lg font-bold">
                        {shockTwo.toFixed(1)}
                      </span>{' '}
                    </p>
                    <Label className="text-foreground text-sm font-light">
                      J
                    </Label>
                  </div>

                  {!hideChargeThree && (
                    <div className="mb-4 flex flex-row items-center justify-center gap-2">
                      <Label>Demais choques</Label>
                      <p className="font-light">
                        <span className="text-tertiary text-lg font-bold">
                          {shockThree.toFixed(1)}
                        </span>{' '}
                      </p>
                      <Label className="text-foreground text-sm font-light">
                        J
                      </Label>
                    </div>
                  )}
                </div>
              )}

              {templateThree && (
                <div className="mx-auto block w-fit">
                  <div className="mb-4 flex flex-row items-center gap-2">
                    <FormField
                      control={form.control}
                      name="dose"
                      render={({ field: { ref, ...rest } }) => (
                        <FormItem className="grid grid-cols-[4rem_auto_auto] items-center">
                          <FormLabel>Dose</FormLabel>
                          <FormControl>
                            <NumberFormatBase
                              className="[&_input]:w-20 [&_input]:text-center"
                              customInput={Input}
                              format={(value) =>
                                formatterDecimal(value, doseFixed)
                              }
                              getCaretBoundary={(formattedValue) => {
                                const boundaryAry = Array.from({
                                  length: formattedValue.length + 1,
                                }).map(() => true);
                                boundaryAry[0] = false;
                                boundaryAry[1] = false;
                                boundaryAry[2] = false;
                                return boundaryAry;
                              }}
                              getInputRef={ref}
                              {...rest}
                            />
                          </FormControl>
                          <FormDescription className="font-light">
                            mg/Kg/dose
                          </FormDescription>
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="mb-4 grid grid-cols-[4.5rem_min-content_max-content] items-center gap-2">
                    <Label>Nebulizar</Label>
                    <p className="font-light">
                      <span className="text-tertiary text-lg font-bold">
                        {neb.toFixed(1)}
                      </span>{' '}
                    </p>
                    <Label className="text-foreground text-sm font-light">
                      {applicationUnitMeasure}
                    </Label>
                  </div>
                </div>
              )}

              {templateFour && (
                <div className="mb-4 flex flex-row items-center justify-center gap-2">
                  <Label>Aplicar</Label>
                  <p className="font-light">
                    <span className="text-tertiary text-lg font-bold">
                      {spray}
                    </span>{' '}
                  </p>
                  <Label className="text-foreground text-sm font-light">
                    {applicationUnitMeasure}
                  </Label>
                </div>
              )}

              {templateFive && (
                <div className="mb-4 flex flex-row items-center justify-center gap-2">
                  <Label>Nebulizar</Label>
                  <p className="font-light">
                    <span className="text-tertiary text-lg font-bold">
                      {inal}
                    </span>{' '}
                  </p>
                  <Label className="text-foreground text-sm font-light">
                    {applicationUnitMeasure}
                  </Label>
                </div>
              )}

              {templateSix && (
                <div className="mx-auto block w-fit">
                  <div className="mb-4 flex flex-row items-center justify-center gap-2">
                    <FormField
                      control={form.control}
                      name="dose"
                      render={({ field: { ref, ...rest } }) => (
                        <FormItem className="grid grid-cols-[4rem_auto_auto] items-center">
                          <FormLabel>Dose</FormLabel>
                          <FormControl>
                            <NumberFormatBase
                              className="[&_input]:w-20 [&_input]:text-center"
                              customInput={Input}
                              format={(value) =>
                                formatterDecimal(value, doseFixed)
                              }
                              getCaretBoundary={(formattedValue) => {
                                const boundaryAry = Array.from({
                                  length: formattedValue.length + 1,
                                }).map(() => true);
                                boundaryAry[0] = false;
                                boundaryAry[1] = false;
                                boundaryAry[2] = false;
                                return boundaryAry;
                              }}
                              getInputRef={ref}
                              {...rest}
                            />
                          </FormControl>
                          <FormDescription className="font-light">
                            {doseUnitMeasure}
                          </FormDescription>
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="mb-4 grid grid-cols-[4rem_min-content_max-content] items-center gap-2">
                    <Label>{applicationText || 'Tomar'}</Label>
                    <p className="font-light">
                      <span className="text-tertiary text-lg font-bold">
                        {comp.toFixed(1)}
                      </span>{' '}
                    </p>
                    <Label className="text-foreground text-sm font-light">
                      {applicationUnitMeasure}
                    </Label>
                  </div>
                </div>
              )}

              {templateSeven && (
                <div>
                  <div className="mb-4 flex flex-row items-center justify-center gap-2">
                    {!templateSevenCalc3 &&
                      !templateSevenCalc4 &&
                      !templateSevenCalc5 &&
                      !templateSevenCalc6 &&
                      !templateSevenCalc7 &&
                      !templateSevenCalc8 &&
                      !templateSevenCalc9 &&
                      !templateSevenCalc10 &&
                      !templateSevenCalc11 && (
                        <Label
                        // fontSize={
                        //   applicationUnitMeasure2 || templateSevenCalc12
                        //     ? undefined
                        //     : 14
                        // }
                        >
                          Aplicar
                        </Label>
                      )}

                    <p className="font-light">
                      <span
                        // fontSize={
                        //   applicationUnitMeasure2 || templateSevenCalc12
                        //     ? undefined
                        //     : 16
                        // }
                        className="text-tertiary text-lg font-bold">
                        {applicationValueThree}
                      </span>{' '}
                    </p>
                    {!templateSevenCalc3 &&
                      !templateSevenCalc4 &&
                      !templateSevenCalc5 &&
                      !templateSevenCalc6 &&
                      !templateSevenCalc7 &&
                      !templateSevenCalc8 &&
                      !templateSevenCalc9 &&
                      !templateSevenCalc10 &&
                      !templateSevenCalc11 && (
                        <Label className="text-foreground text-sm font-light">
                          {applicationUnitMeasure}
                        </Label>
                      )}
                  </div>
                  {applicationUnitMeasure2 && (
                    <div className="mb-4 flex flex-row items-center justify-center gap-2">
                      <Label className="text-foreground text-sm font-light">
                        {applicationUnitMeasure2}
                      </Label>
                    </div>
                  )}
                </div>
              )}

              {templateEight && (
                <div>
                  <div className="mb-4 flex flex-col items-center">
                    <Label className="italic">Streptococcus do grupo A</Label>
                    <div className="flex flex-row items-center justify-center gap-2">
                      <Label>Aplicar</Label>
                      <p className="font-light">
                        <span className="text-tertiary text-lg font-bold">
                          {uValueOne}
                        </span>{' '}
                      </p>
                      <Label className="text-foreground text-sm font-light">
                        U, IM
                      </Label>
                    </div>
                    <Label className="text-foreground text-sm font-light">
                      dose única
                    </Label>
                  </div>

                  <div className="mb-4 flex flex-col items-center">
                    <Label className="italic">Sífilis recente</Label>
                    <div className="flex flex-row items-center justify-center gap-2">
                      <Label>Aplicar</Label>
                      <p className="font-light">
                        <span className="text-tertiary text-lg font-bold">
                          {uValueTwo}
                        </span>{' '}
                      </p>
                      <Label className="text-foreground text-sm font-light">
                        U, IM
                      </Label>
                    </div>
                    <Label className="text-foreground text-sm font-light">
                      dose única
                    </Label>
                  </div>

                  <div className="mb-4 flex flex-col items-center">
                    <Label className="italic">Sífilis tardia</Label>
                    <div className="flex flex-row items-center justify-center gap-2">
                      <Label>Aplicar</Label>
                      <p className="font-light">
                        <span className="text-tertiary text-lg font-bold">
                          {uValueTwo}
                        </span>{' '}
                      </p>
                      <Label className="text-foreground text-sm font-light">
                        U, IM
                      </Label>
                    </div>
                    <Label className="text-foreground text-sm font-light">
                      1x/semana, por 3 semanas
                    </Label>
                  </div>
                </div>
              )}
            </form>
          </Form>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

export default Content;
